/**
 * Leitura do checklist da reserva (P3) para as telas — só apresentação. Status, validade,
 * percentual e contadores vêm do backend (`ChecklistReservaService`); aqui nada é calculado,
 * só escolhido o texto a exibir.
 */
import type { StatusItemChecklistAtual } from '@/types/checklist'
import {
  CATEGORIA_DOCUMENTO_LABEL,
  type ChecklistReserva,
  type GrupoChecklistReserva,
  type ItemChecklistReserva,
} from '@/types/estruturacao'

/** Status em que o consultor ainda troca arquivos da reserva (janela do AN-4 §5). */
export const STATUS_JANELA_CONSULTOR = ['RASCUNHO', 'COM_PENDENCIA']

export const rotuloDoItem = (item: Pick<ItemChecklistReserva, 'rotulo' | 'label'>): string =>
  item.rotulo || item.label

/**
 * Status do item como o backend mandou. Só uma resposta anterior à P3 (sem `status`) cai no
 * `presente` de compatibilidade — que o próprio backend define como RECEBIDO ou VALIDADO.
 */
export const statusDoItem = (item: Pick<ItemChecklistReserva, 'status' | 'presente'>): StatusItemChecklistAtual =>
  item.status ?? (item.presente ? 'RECEBIDO' : 'FALTANDO')

/** Rótulo do grupo: categoria livre da versão; o enum legado só quando o backend não mandou rótulo. */
export const rotuloDoGrupo = (grupo: GrupoChecklistReserva): string =>
  grupo.rotulo ||
  (grupo.categoria ? CATEGORIA_DOCUMENTO_LABEL[grupo.categoria] ?? grupo.categoria : null) ||
  grupo.codigo ||
  'Outros documentos'

export const chaveDoGrupo = (grupo: GrupoChecklistReserva, indice: number): string =>
  grupo.codigo ?? grupo.categoria ?? `grupo-${indice}`

/** Grupos com itens, na ORDEM da versão (o backend já ordena; a tela não reordena). */
export const gruposComItens = (checklist: ChecklistReserva | null | undefined): GrupoChecklistReserva[] =>
  (checklist?.categorias ?? []).filter((g) => g.itens.length > 0)

export const ehSolicitacaoCuradoria = (item: Pick<ItemChecklistReserva, 'origem' | 'chave'>): boolean =>
  item.origem === 'SOLICITACAO_CURADORIA' || (!!item.chave && item.chave.startsWith('sol-'))

/**
 * Algum item "Em verificação" com arquivo ainda sendo lido (PENDENTE/PROCESSANDO) — a tela relê
 * enquanto houver. Falha de leitura (ERRO) não se resolve sozinha: não adianta reler.
 */
export const temItemEmVerificacao = (checklist: ChecklistReserva | null | undefined): boolean =>
  (checklist?.categorias ?? []).some((g) =>
    g.itens.some(
      (i) =>
        i.status === 'QUARENTENA' &&
        ((i.situacaoArquivos ?? []).length === 0 ||
          (i.situacaoArquivos ?? []).some((s) => s === 'PENDENTE' || s === 'PROCESSANDO')),
    ),
  )

/** Há item básico ainda faltando — só então o botão "Reaproveitar básicos" faz sentido. */
export const temBasicoFaltando = (checklist: ChecklistReserva | null | undefined): boolean =>
  (checklist?.categorias ?? []).some((g) => g.itens.some((i) => i.basico && i.status === 'FALTANDO'))
